import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { EntityFilesContent, EntityNotesContent } from '@popety-io/ui'
import { apiClient } from '../../services/apiClient'
import {
  toSharedEntityFiles,
  toSharedEntityNotes,
  type SharedFile,
  type SharedNote,
} from './mapSharedAttachments'

export type SharedAttachmentKind = 'land' | 'grouping' | 'building' | 'estimate'

const ENDPOINT: Record<SharedAttachmentKind, string> = {
  land: 'land-analysis-public-share',
  grouping: 'land-grouping-public-share',
  building: 'building-analysis-public-share',
  estimate: 'estimate-public-shares',
}

const Frame = styled.div`
  height: calc(100vh - 200px);
  min-height: 420px;
  display: flex;
  flex-direction: column;
`

const fileEndpoint = (
  kind: SharedAttachmentKind,
  hash: string,
  fileId: string
) => `/${ENDPOINT[kind]}/public/${hash}/files/${fileId}`

export const SharedNotesSection: React.FC<{
  notes?: SharedNote[] | null
}> = ({ notes }) => {
  const { t } = useTranslation('common')
  const [searchQuery, setSearchQuery] = useState('')
  const entityNotes = useMemo(() => toSharedEntityNotes(notes), [notes])

  return (
    <Frame>
      <EntityNotesContent
        entityId="shared"
        entityType="land"
        notes={entityNotes}
        loading={false}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isAddingNote={false}
        setIsAddingNote={() => undefined}
        editingNoteId={null}
        setEditingNoteId={() => undefined}
        deleteConfirmId={null}
        setDeleteConfirmId={() => undefined}
        readOnly
        translations={{
          title: t('sidebar.items.notes'),
          searchPlaceholder: t('attachments.searchNotes'),
          clearSearch: t('attachments.clearSearch'),
          emptyTitle: t('attachments.notesEmptyTitle'),
          emptyDescription: t('attachments.notesEmptyDescription'),
          noResultsTitle: t('attachments.noResultsTitle'),
          noResultsDescription: t('attachments.noResultsDescription'),
        }}
      />
    </Frame>
  )
}

export const SharedFilesSection: React.FC<{
  files?: SharedFile[] | null
  hash: string
  kind: SharedAttachmentKind
}> = ({ files, hash, kind }) => {
  const { t } = useTranslation('common')
  const entityFiles = useMemo(() => toSharedEntityFiles(files), [files])
  const fileService = useMemo(
    () => ({
      downloadFile: async (fileId: string) => {
        await apiClient.downloadFile(fileEndpoint(kind, hash, fileId))
      },
      previewFile: (fileId: string) =>
        apiClient.previewFile(fileEndpoint(kind, hash, fileId)),
    }),
    [hash, kind]
  )

  return (
    <Frame>
      <EntityFilesContent
        entityId="shared"
        entityType={kind === 'building' ? 'building' : 'land'}
        files={entityFiles}
        loading={false}
        error={null}
        activeCategory="all"
        fileService={fileService}
        readOnly
        emptyTitle={t('attachments.filesEmptyTitle')}
        emptyDescription={t('attachments.filesEmptyDescription')}
      />
    </Frame>
  )
}
